import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { ComoRegistrationModalConfig as ComoRegistrationModalContentConfig } from '@front-platform/content-builder';
import {
  comoRegistrationModalResolveConfig,
  isLinkValue,
  resolveAccentFieldColors,
  resolveLinkValueToUrl,
} from '@front-platform/content-builder';
import { Component, computed, inject, OnInit, input, model } from '@angular/core';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';

import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { ModalsService } from '@core/services/modals/modals.service';
import { ModalStoreSelector } from '@common/selectors/modal.selector';
import { RegistrationStep } from '@shared/modals/como/como-registration-modal/registration-step.enum';
import { Router } from '@angular/router';
import { LinkNavigationService } from '@core/services/link-navigation/link-navigation.service';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { SettingsParametersState } from '@store/settings';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { ContentBuilderFieldColors } from '@shared/interfaces/content-builder.interface';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { map, Observable } from 'rxjs';
import { ComoPluginType } from '@core/models/plugin.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { FidelityType } from '@shared/modals/kiosk-fidelity-registration/fidelity-type.enum';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { GetModalInterface, ModalConfig } from '@core/services/modals/modals-util.type';
import { resolvePhoneDefaultRegionCode } from '@common/utils/phone-default-region.util';
import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { toShopRef } from '@core/services/link-navigation/link-navigation.service';

export type ComoRegistrationModalConfig = ModalConfig<{
  handle: typeof ComoRegistrationModalComponent.handle;
  inputs: {
    goToURLOnClose: string;
    goToKioskRegister?: boolean;
    activeStep?: RegistrationStep;
    accentColor?: ContentBuilderFieldColors;
    phone?: string;
  };
}>;
export interface ComoRegistrationModalRuntimeConfig {}

export type ComoRegistrationModalConfigInput = Partial<
  Omit<ComoRegistrationModalContentConfig, keyof ComoRegistrationModalRuntimeConfig> &
    ComoRegistrationModalRuntimeConfig
>;
export type ComoRegistrationModalResolvedConfig = Omit<
  ComoRegistrationModalContentConfig,
  keyof ComoRegistrationModalRuntimeConfig
> &
  Partial<ComoRegistrationModalRuntimeConfig>;
export interface ComoRegistrationModalContract extends ConfigComponentContract<
  ComoRegistrationModalConfigInput,
  ComoRegistrationModalResolvedConfig
> {}

@Component({
  selector: 'app-como-registration-modal',
  templateUrl: './como-registration-modal.component.html',
  styleUrls: ['./como-registration-modal.component.scss'],
  standalone: false,
})
export class ComoRegistrationModalComponent
  extends DestroyerBase
  implements OnInit, GetModalInterface<ComoRegistrationModalConfig>, ComoRegistrationModalContract
{
  readonly config = input<ComoRegistrationModalConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    ComoRegistrationModalContentConfig,
    ComoRegistrationModalRuntimeConfig
  >(comoRegistrationModalResolveConfig, () => ({ config: this.config() }));
  static handle = 'como-registration-modal' as const;

  phone = model<string>('');
  keyboardOffset = 0; // For keyboard-aware positioning

  registrationStep = RegistrationStep;

  protected readonly FidelityType = FidelityType;

  private readonly modalStoreSelector = inject(ModalStoreSelector);

  constructor(
    private readonly modalsService: ModalsService,
    private readonly router: Router,
    private readonly routerHelper: RouterHelperService,
    private readonly settingsSelector: SettingsStoreSelector,
    private readonly marketplaceSelector: MarketplaceStoreSelector,
    private readonly shopSelector: ShopStoreSelector,
    private readonly linkNavigation: LinkNavigationService,
    private readonly analyticsService: AnalyticsService,
  ) {
    super();
  }
  public readonly goToURLOnClose = computed(() =>
    this.resolveRedirectUrl(this.resolvedConfig()['go_to_u_r_l_on_close']),
  );
  public readonly goToKioskRegister = computed(
    () => this.resolvedConfig()['go_to_kiosk_register'] as unknown as boolean,
  );
  public readonly activeStep = model<RegistrationStep>(this.registrationStep.STEP_PHONE);
  public readonly accentColor = computed(() => {
    const cfg = this.resolvedConfig() as Record<string, unknown>;
    return resolveAccentFieldColors(cfg, this.config() as Record<string, unknown>);
  });
  public readonly backgroundColor = computed(
    () => this.resolvedConfig()['background_color'] as unknown as IContentBuilderFieldColor,
  );

  private readonly comoPlugin$: Observable<ComoPluginType | undefined> =
    this.marketplaceSelector.selectMarketplacePlugins$.pipe(
      map(plugins => plugins?.find(plugin => plugin.type === 'como') as ComoPluginType | undefined),
    );

  protected readonly useLocalFormat = toSignal(
    this.comoPlugin$.pipe(map(plugin => !!(plugin?.phone_number_format === 'LOCAL'))),
    { initialValue: true },
  );
  protected readonly defaultRegionCode = computed(() => {
    const plugins = this.marketplaceSelector.selectMarketplacePlugins();
    const plugin = plugins?.find(p => p.type === 'como') as ComoPluginType | undefined;
    return resolvePhoneDefaultRegionCode(
      plugin?.phone_number_default_region_code,
      this.marketplaceSelector.selectMarketplaceCountrySignal(),
    );
  });

  ngOnInit() {
    this.getParameters();
    const configuredPhone = this.resolvedConfig()['phone'] as string | undefined;
    if (configuredPhone) {
      this.phone.set(configuredPhone);
    }
    if (this.goToKioskRegister()) {
      this.onGoToRegisterStep();
      return;
    }
    const configuredStep = this.resolvedConfig()['active_step'] as RegistrationStep | undefined;
    if (configuredStep) {
      this.activeStep.set(configuredStep);
    }
  }

  onClose(): void {
    this.modalsService.close(ComoRegistrationModalComponent.handle);
  }

  onComplete(): void {
    const runtimeRedirect = this.runtimeRedirectRaw();
    const configRedirect = this.resolvedConfig()['go_to_u_r_l_on_close'];
    const redirect = runtimeRedirect ?? configRedirect;
    const destinationLabel =
      typeof redirect === 'string'
        ? redirect
        : isLinkValue(redirect)
          ? redirect.kind === 'modal'
            ? `modal:${redirect.handle}`
            : resolveLinkValueToUrl(redirect, {
                resolveActiveShop: () => toShopRef(this.shopSelector.selectActive()),
              })
          : null;
    this.analyticsService.log({
      level: destinationLabel ? 'info' : 'warn',
      label: '[DOOD-3204] ComoRegistrationModal.onComplete',
      message: {
        has_runtime_destination: runtimeRedirect != null && runtimeRedirect !== '',
        has_config_destination: configRedirect != null && configRedirect !== '',
        navigates: !!destinationLabel,
        destination: destinationLabel,
        current_path: window.location.pathname,
      },
    });
    this.onClose();
    this.navigateAfterComplete(redirect);
  }

  private runtimeRedirectRaw(): unknown {
    const data = this.modalStoreSelector.modals.find(
      modal => modal.handle === ComoRegistrationModalComponent.handle,
    )?.data;
    if (!data || typeof data !== 'object') {
      return undefined;
    }
    const record = data as Record<string, unknown>;
    if (record['goToURLOnClose'] !== undefined) {
      return record['goToURLOnClose'];
    }
    if (record['go_to_u_r_l_on_close'] !== undefined) {
      return record['go_to_u_r_l_on_close'];
    }
    return undefined;
  }

  private navigateAfterComplete(redirect: unknown): void {
    if (redirect == null || redirect === '') {
      return;
    }
    if (isLinkValue(redirect)) {
      this.linkNavigation.navigate(redirect);
      return;
    }
    if (typeof redirect !== 'string') {
      return;
    }
    const trimmed = redirect.trim();
    if (!trimmed) {
      return;
    }
    if (this.modalsService.isModalLink(trimmed)) {
      this.linkNavigation.navigate(trimmed);
      return;
    }
    this.router.navigate([this.routerHelper.translateRoute(trimmed)]);
  }

  onGoToOtpStep(): void {
    this.activeStep.set(this.registrationStep.STEP_OTP);
  }

  onGoToAccountNotFoundStep() {
    this.activeStep.set(this.registrationStep.STEP_ACCOUNT_NOT_FOUND);
  }

  onGoToPhoneStep() {
    this.activeStep.set(this.registrationStep.STEP_PHONE);
  }

  onGoToAuthenticationStep() {
    this.activeStep.set(this.registrationStep.STEP_AUTHENTICATION);
  }

  onGoToConfirmationStep() {
    this.activeStep.set(this.registrationStep.STEP_KIOSK_CONFIRMATION);
  }

  onGoToRegisterStep() {
    this.activeStep.set(this.registrationStep.STEP_KIOSK_REGISTER);
  }

  onGoToKioskRegisterConfirmationStep() {
    this.activeStep.set(this.registrationStep.STEP_KIOSK_REGISTER_CONFIRMATION);
  }

  onGoToKioskAccountNotFoundStep() {
    this.activeStep.set(this.registrationStep.STEP_KIOSK_ACCOUNT_NOT_FOUND);
  }

  getParameters(): SettingsParametersState {
    return this.settingsSelector.parameters;
  }

  private resolveRedirectUrl(raw: unknown): string {
    if (typeof raw === 'string') {
      return raw.trim();
    }
    if (!isLinkValue(raw) || raw.kind === 'modal') {
      return '';
    }
    const resolvedUrl = resolveLinkValueToUrl(raw, {
      resolveActiveShop: () => toShopRef(this.shopSelector.selectActive()),
    });
    if (raw.kind === 'page' && !resolvedUrl) {
      return '/';
    }
    return resolvedUrl.trim();
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(ComoRegistrationModalComponent, {
  'keyAliases': { 'go_to_urlon_close': 'go_to_u_r_l_on_close' },
});
